import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Bot,
  Clock,
  Database,
  ExternalLink,
  HardDrive,
  Link2,
  Map as MapIcon,
  Play,
  RefreshCw,
  Save,
  Send,
  Server,
  Square,
  Trash2,
  UserPlus,
  Users
} from 'lucide-react';
import type { AdminCommandSnapshot, AdminSlotAction, BotApi } from '../adapters/BotApi';
import type {
  AgentHost,
  AuditEvent,
  BlueStacksProfileHealth,
  BotSlot,
  DesiredBotState,
  DesiredWorkerState,
  HostCapacitySnapshot,
  LicensePlan,
  LinkingChatMessage,
  SaveSlotRequest,
  UserProfile
} from '../domain/types';
import { EmptyState, ErrorState } from './components';
import { OperationModal } from './OperationModal';

const defaultModules = { collect: true, train_troops: true, auto_hunt: true, intelligence: false };
const emptyUsers: UserProfile[] = [];
const emptySlots: BotSlot[] = [];
const emptyAgentHosts: AgentHost[] = [];
const emptyAuditEvents: AuditEvent[] = [];
const adminCommandTerminalStatuses = new Set(['completed', 'failed', 'rejected', 'duplicate', 'cancelled', 'timeout']);
const auditPageSize = 300;
const adminCommandDefaultTimeoutMs = 240000;
const adminWorkerCommandTimeoutMs = 420000;

interface WorkerOperationState {
  action: AdminSlotAction;
  agentId: string;
  slotKey?: string;
  commandId?: string;
  status: 'preparing' | 'timeout' | AdminCommandSnapshot['status'];
  message: string;
  detail?: string;
  error?: string | null;
  startedAt: number;
}

export function AdminPage({
  api,
  onOpenDashboardSlot
}: {
  api: BotApi;
  onOpenDashboardSlot?: (slotId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [selectedListSlotId, setSelectedListSlotId] = useState<string | null>(null);
  const [auditSlotId, setAuditSlotId] = useState<string>('all');
  const [auditPage, setAuditPage] = useState(0);
  const [slotEditorOpen, setSlotEditorOpen] = useState(false);

  // Servidor seleccionado como contexto primario — persiste en sessionStorage.
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(() => {
    try { return sessionStorage.getItem('admin_selected_agent_id') || null; } catch { return null; }
  });

  const [workerControlSlotKey, setWorkerControlSlotKey] = useState('');
  const [workerOperation, setWorkerOperation] = useState<WorkerOperationState | null>(null);
  const [form, setForm] = useState<SaveSlotRequest>({
    slotKey: '',
    userId: '',
    label: 'Hosted Test',
    agentId: 'local-dev',
    regionId: '',
    licenseStatus: 'trial',
    licensePlan: 'trial',
    linked: false,
    enabledModules: defaultModules
  });
  const [message, setMessage] = useState('');

  const overviewQuery = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.getAdminOverview(),
    refetchInterval: 5000
  });

  const users = overviewQuery.data?.users ?? emptyUsers;
  const slots = overviewQuery.data?.slots ?? emptySlots;
  const agentHosts = overviewQuery.data?.agentHosts ?? emptyAgentHosts;
  const overviewAuditEvents = overviewQuery.data?.auditEvents ?? emptyAuditEvents;

  const auditQuery = useQuery({
    queryKey: ['admin-audit-events', selectedAgentId, auditSlotId, auditPage],
    queryFn: () => api.getAuditEvents!({
      limit: auditPageSize,
      offset: auditPage * auditPageSize,
      slotId: auditSlotId === 'all' ? null : auditSlotId,
      agentId: auditSlotId === 'all' ? selectedAgentId : null
    }),
    enabled: Boolean(api.getAuditEvents && (auditSlotId !== 'all' || selectedAgentId)),
    refetchInterval: auditPage === 0 ? 5000 : false
  });

  const auditEvents = api.getAuditEvents ? (auditQuery.data?.events ?? emptyAuditEvents) : overviewAuditEvents;
  const auditTotal = api.getAuditEvents ? auditQuery.data?.total : overviewAuditEvents.length;
  const auditOffset = api.getAuditEvents ? (auditQuery.data?.offset ?? auditPage * auditPageSize) : 0;
  const auditLimit = api.getAuditEvents ? (auditQuery.data?.limit ?? auditPageSize) : overviewAuditEvents.length;
  const auditHasMore = api.getAuditEvents ? Boolean(auditQuery.data?.hasMore) : false;

  const assistanceQuery = useQuery({
    queryKey: ['admin-linking-assistance'],
    queryFn: () => api.listLinkingAssistanceRequests!(),
    enabled: Boolean(api.listLinkingAssistanceRequests),
    refetchInterval: 5000
  });
  const assistanceSlotIds = useMemo(
    () => new Set((assistanceQuery.data || []).map((session) => session.slotId)),
    [assistanceQuery.data]
  );

  // Slots y opciones locales filtradas por el servidor seleccionado.
  const selectedAgentSlots = useMemo(
    () => (selectedAgentId ? slots.filter((s) => s.agentId === selectedAgentId) : slots),
    [slots, selectedAgentId]
  );
  const localSlotOptions = useMemo(() => localSlotOptionsFromHosts(agentHosts), [agentHosts]);
  const selectedAgentLocalSlotOptions = useMemo(
    () => (selectedAgentId ? localSlotOptions.filter((s) => s.agentId === selectedAgentId) : localSlotOptions),
    [localSlotOptions, selectedAgentId]
  );

  // Slots asignados en Supabase para el servidor seleccionado (para auto-assign y disponibilidad).
  const selectedAgentAssignedSlotKeys = useMemo(
    () => new Set(selectedAgentSlots.map((slot) => slot.slotKey)),
    [selectedAgentSlots]
  );

  const pendingUsers = users.filter((user) => !slots.some((slot) => slot.userEmail === user.email));
  const pendingCustomerUsers = pendingUsers.filter((user) => user.role !== 'admin');
  const assignedUsers = users.filter((user) => slots.some((slot) => slot.userEmail === user.email));
  const orderedUsers = [...pendingUsers, ...assignedUsers];

  // availableLocalSlots y readyAvailableLocalSlots usan el servidor seleccionado.
  const availableLocalSlots = useMemo(
    () => selectedAgentLocalSlotOptions.filter((slot) => !selectedAgentAssignedSlotKeys.has(slot.slotKey)),
    [selectedAgentAssignedSlotKeys, selectedAgentLocalSlotOptions]
  );
  const readyAvailableLocalSlots = useMemo(
    () => availableLocalSlots.filter((slot) => slot.health === 'ready'),
    [availableLocalSlots]
  );
  const nextAutoUser = pendingCustomerUsers[0] || pendingUsers[0] || null;
  const nextAutoSlot = readyAvailableLocalSlots[0] || availableLocalSlots[0] || null;

  // commandSlotKeys filtra por servidor seleccionado para el formulario.
  const commandSlotKeys = useMemo(
    () => mergeSlotKeys(
      selectedAgentLocalSlotOptions.map((slot) => slot.slotKey),
      selectedAgentSlots.map((slot) => slot.slotKey)
    ),
    [selectedAgentLocalSlotOptions, selectedAgentSlots]
  );

  // selectedFormSlot busca por (slotKey, agentId) para evitar ambigüedad entre servidores.
  const selectedFormSlot = useMemo(
    () => slots.find((slot) => slot.slotKey === form.slotKey && slot.agentId === form.agentId),
    [form.slotKey, form.agentId, slots]
  );
  const selectedLocalSlotOption = useMemo(
    () => localSlotOptions.find((slot) => slot.slotKey === form.slotKey && slot.agentId === form.agentId) || null,
    [form.slotKey, form.agentId, localSlotOptions]
  );
  const selectedListSlot = useMemo(
    () => slots.find((slot) => slot.id === selectedListSlotId) || null,
    [selectedListSlotId, slots]
  );
  const selectedHost = useMemo(
    () => agentHosts.find((host) => host.agentId === (form.agentId || selectedAgentId)) || agentHosts[0],
    [agentHosts, form.agentId, selectedAgentId]
  );
  const selectedHostCapacity = selectedHost?.inventory?.capacity;
  const displayedFreeSlots = selectedHostCapacity?.assignment_known === false
    ? availableLocalSlots.length
    : Number(selectedHostCapacity?.free_count ?? availableLocalSlots.length);
  const displayedCapacityStatus = selectedHostCapacity?.status || (displayedFreeSlots ? 'ready' : 'needs_capacity');

  // workerControlHost y workerControlSlots usan selectedAgentId (estado compartido).
  const workerControlHost = useMemo(
    () => agentHosts.find((host) => host.agentId === selectedAgentId) || agentHosts[0],
    [agentHosts, selectedAgentId]
  );
  const workerControlSlots = useMemo(
    () => localSlotOptions.filter((slot) => slot.agentId === workerControlHost?.agentId),
    [localSlotOptions, workerControlHost?.agentId]
  );
  const workerControlAvailable = api.mode === 'supabase' && Boolean(api.sendAdminSlotCommand);
  const workerOperationActive = Boolean(
    workerOperation && !adminCommandTerminalStatuses.has(workerOperation.status)
  );
  const provisioning = useMemo(
    () => buildProvisioningChecklist({
      slot: selectedFormSlot,
      host: selectedHost,
      commandSlotKeys,
      formSlotKey: form.slotKey
    }),
    [selectedFormSlot, selectedHost, commandSlotKeys, form.slotKey]
  );
  const canSaveAssignment = Boolean(form.slotKey && form.agentId.trim() && form.label.trim());
  const canAutoAssign = Boolean(nextAutoUser && nextAutoSlot);

  // Contadores globales (todos los servidores) para la barra superior.
  const readySlots = slots.filter((slot) => slot.linked && ['active', 'trial'].includes(slot.licenseStatus)).length;

  // Inicializa selectedAgentId con el primer host disponible.
  useEffect(() => {
    if (selectedAgentId && agentHosts.some((h) => h.agentId === selectedAgentId)) return;
    const firstAgentId = agentHosts[0]?.agentId ?? null;
    if (firstAgentId === selectedAgentId) return;
    setSelectedAgentId(firstAgentId);
    try { if (firstAgentId) sessionStorage.setItem('admin_selected_agent_id', firstAgentId); } catch {}
  }, [agentHosts, selectedAgentId]);

  // Sincroniza form.agentId con selectedAgentId.
  useEffect(() => {
    if (!selectedAgentId) return;
    setForm((c) => (c.agentId === selectedAgentId ? c : { ...c, agentId: selectedAgentId }));
  }, [selectedAgentId]);

  useEffect(() => {
    if (commandSlotKeys.includes(form.slotKey)) return;
    const nextSlotKey = commandSlotKeys[0] || '';
    if (nextSlotKey === form.slotKey) return;
    setForm((current) => ({ ...current, slotKey: nextSlotKey }));
  }, [commandSlotKeys, form.slotKey]);

  useEffect(() => {
    if (selectedListSlotId) {
      setAuditSlotId(selectedListSlotId);
      setAuditPage(0);
    }
  }, [selectedListSlotId]);

  useEffect(() => {
    if (!selectedAgentSlots.length) {
      if (auditSlotId !== 'all') {
        setAuditSlotId('all');
        setAuditPage(0);
      }
      return;
    }
    if (auditSlotId === 'all') return;
    if (auditSlotId && selectedAgentSlots.some((slot) => slot.id === auditSlotId)) return;
    setAuditSlotId('all');
    setAuditPage(0);
  }, [auditSlotId, selectedAgentSlots]);

  useEffect(() => {
    if (workerControlSlotKey && workerControlSlots.some((slot) => slot.slotKey === workerControlSlotKey)) return;
    setWorkerControlSlotKey(workerControlSlots[0]?.slotKey || '');
  }, [workerControlSlotKey, workerControlSlots]);

  const saveMutation = useMutation({
    mutationFn: () => api.saveSlot(normalizeSaveSlotRequest(form, selectedFormSlot)),
    onSuccess: async (slot) => {
      setMessage(`Slot guardado: ${slot.slotKey}`);
      setSelectedListSlotId(slot.id);
      setSlotEditorOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : String(error))
  });
  const autoAssignMutation = useMutation({
    mutationFn: async () => {
      if (api.assignNextAvailableSlot) {
        return api.assignNextAvailableSlot(selectedAgentId || undefined);
      }
      if (!nextAutoUser) throw new Error('No hay usuarios pendientes para asignar');
      if (!nextAutoSlot) throw new Error('No hay slots locales disponibles para asignar');
      return api.saveSlot({
        slotKey: nextAutoSlot.slotKey,
        userId: nextAutoUser.id,
        label: defaultSlotLabel(nextAutoUser, nextAutoSlot),
        agentId: nextAutoSlot.agentId,
        regionId: '',
        licenseStatus: 'trial',
        licensePlan: 'trial',
        linked: false,
        enabledModules: defaultModules
      });
    },
    onSuccess: async (slot) => {
      setMessage(`Asignacion automatica creada: ${slot.slotKey}. Pendiente de vincular cuenta del juego.`);
      setSelectedListSlotId(slot.id);
      setSlotEditorOpen(true);
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : String(error))
  });
  const releaseMutation = useMutation({
    mutationFn: (slotId: string) => api.releaseSlot(slotId),
    onSuccess: async () => {
      setMessage('Slot eliminado. El usuario queda pendiente de asignacion.');
      setSelectedListSlotId(null);
      setForm((current) => ({ ...current, userId: '', label: 'Hosted Test', linked: false }));
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : String(error))
  });
  const desiredStateMutation = useMutation({
    mutationFn: (payload: {
      slotId: string;
      desiredWorkerState: DesiredWorkerState;
      desiredBotState: DesiredBotState;
      desiredReason: string;
    }) => {
      if (!api.updateSlotDesiredState) throw new Error('El API actual no permite actualizar estado deseado');
      return api.updateSlotDesiredState(payload.slotId, {
        desiredWorkerState: payload.desiredWorkerState,
        desiredBotState: payload.desiredBotState,
        desiredReason: payload.desiredReason
      });
    },
    onSuccess: async (slot) => {
      setMessage(`Estado deseado actualizado: ${slot.slotKey}`);
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : String(error))
  });
  const deleteLocalSlotMutation = useMutation({
    mutationFn: async (payload: { slot_id: string; agent_id: string }) => {
      if (api.sendAdminSlotCommand) {
        await api.sendAdminSlotCommand('admin_delete_slot', payload);
        return;
      }
      await api.releaseSlot(payload.slot_id);
    },
    onSuccess: async () => {
      setMessage('Comando enviado al agente. El slot desaparecera en unos segundos.');
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : String(error))
  });
  const workerControlMutation = useMutation({
    mutationFn: async (payload: { action: AdminSlotAction; agentId: string; slotKey?: string }) => {
      if (!api.sendAdminSlotCommand) throw new Error('El API actual no permite comandos admin remotos');
      const receipt = await api.sendAdminSlotCommand(payload.action, {
        agent_id: payload.agentId,
        ...(payload.slotKey ? { slot_key: payload.slotKey, slot_id: payload.slotKey } : {})
      });
      setWorkerOperation((current) => ({
        action: payload.action,
        agentId: payload.agentId,
        slotKey: payload.slotKey,
        commandId: receipt.commandId,
        status: 'queued',
        message: workerOperationMessage(payload.action, 'queued'),
        detail: `command_id=${receipt.commandId}`,
        startedAt: current?.startedAt || Date.now()
      }));
      if (!api.getAdminCommandStatus) return null;
      return waitForAdminCommandStatus(api, receipt.commandId, payload.action, (snapshot) => {
        setWorkerOperation((current) => ({
          action: payload.action,
          agentId: payload.agentId,
          slotKey: payload.slotKey,
          commandId: receipt.commandId,
          status: snapshot.status,
          message: workerOperationMessage(payload.action, snapshot.status),
          detail: workerOperationDetail(snapshot),
          error: snapshot.error,
          startedAt: current?.startedAt || Date.now()
        }));
      });
    },
    onSuccess: async (snapshot, payload) => {
      const finalStatus = snapshot?.status || 'completed';
      setMessage(
        payload.slotKey
          ? `Comando ${finalStatus}: ${payload.agentId} ${payload.action} ${payload.slotKey}`
          : `Comando ${finalStatus}: ${payload.agentId} ${payload.action}`
      );
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    },
    onError: (error, payload) => {
      const message = error instanceof Error ? error.message : String(error);
      setMessage(message);
      setWorkerOperation((current) => ({
        action: payload.action,
        agentId: payload.agentId,
        slotKey: payload.slotKey,
        commandId: current?.commandId,
        status: message.toLowerCase().includes('timeout') ? 'timeout' : 'failed',
        message: message.toLowerCase().includes('timeout')
          ? 'Tiempo de espera agotado'
          : 'El comando no pudo completarse',
        detail: current?.detail,
        error: message,
        startedAt: current?.startedAt || Date.now()
      }));
    }
  });

  function selectAgent(agentId: string) {
    setSelectedAgentId(agentId);
    try { sessionStorage.setItem('admin_selected_agent_id', agentId); } catch {}
    setForm((c) => ({ ...c, agentId, slotKey: '' }));
    setWorkerControlSlotKey('');
  }

  function fillSlot(slot: BotSlot) {
    const user = users.find((item) => item.email === slot.userEmail);
    setSelectedListSlotId(slot.id);
    setSlotEditorOpen(true);
    setForm({
      slotKey: slot.slotKey,
      userId: user?.id || '',
      label: slot.label,
      agentId: slot.agentId,
      regionId: slot.regionId || '',
      licenseStatus: slot.licenseStatus,
      licensePlan: licensePlanFromSlot(slot),
      linked: slot.linked,
      enabledModules: slot.enabledModules
    });
  }

  function fillLocalSlot(option: LocalSlotOption) {
    const assignedSlot = selectedAgentSlots.find((slot) => slot.slotKey === option.slotKey);
    if (assignedSlot) {
      fillSlot(assignedSlot);
      return;
    }
    setSelectedListSlotId(null);
    setForm((current) => ({
      ...current,
      slotKey: option.slotKey,
      agentId: option.agentId,
      label: current.label || option.slotKey
    }));
    setSlotEditorOpen(true);
    setMessage(`Slot local seleccionado: ${option.slotKey}`);
  }

  function setSlotKey(slotKey: string) {
    const localOption = localSlotOptions.find((slot) => slot.slotKey === slotKey && slot.agentId === form.agentId);
    const assignedSlot = selectedAgentSlots.find((slot) => slot.slotKey === slotKey);
    if (assignedSlot) {
      fillSlot(assignedSlot);
      return;
    }
    setSelectedListSlotId(null);
    setForm((current) => ({
      ...current,
      slotKey,
      agentId: localOption?.agentId || current.agentId
    }));
    setSlotEditorOpen(true);
  }

  function fillUser(user: UserProfile) {
    const existingSlot = slots.find((slot) => slot.userEmail === user.email);
    if (existingSlot) {
      fillSlot(existingSlot);
      return;
    }
    setForm((current) => ({
      ...current,
      userId: user.id,
      label: current.label || user.email.split('@')[0]
    }));
    setSlotEditorOpen(true);
    setMessage(`Usuario seleccionado: ${user.email}`);
  }

  function setModule(module: keyof SaveSlotRequest['enabledModules'], value: boolean) {
    setForm((current) => ({
      ...current,
      enabledModules: { ...current.enabledModules, [module]: value }
    }));
  }

  function reserveSlot() {
    setForm((current) => ({
      ...current,
      licenseStatus: current.licenseStatus === 'inactive' ? 'trial' : current.licenseStatus,
      licensePlan: current.licensePlan || 'trial',
      linked: false
    }));
    setMessage('Modo reserva: licencia preparada, cuenta del juego pendiente de vincular.');
  }

  function markLinked() {
    setForm((current) => ({
      ...current,
      licenseStatus: current.licenseStatus === 'inactive' ? 'trial' : current.licenseStatus,
      licensePlan: current.licensePlan || 'trial',
      linked: true
    }));
    setMessage('Modo vinculado: usar solo tras confirmar que la cuenta del juego esta lista.');
  }

  function releaseSelectedSlot() {
    if (!selectedListSlot) {
      setMessage('Selecciona un slot antes de liberar.');
      return;
    }
    const confirmed = window.confirm(`Eliminar slot ${selectedListSlot.label} (${selectedListSlot.slotKey})? Esta accion no se puede deshacer.`);
    if (!confirmed) return;
    releaseMutation.mutate(selectedListSlot.id);
  }

  function setDesiredForSelected(
    desiredWorkerState: DesiredWorkerState,
    desiredBotState: DesiredBotState,
    desiredReason: string
  ) {
    if (!selectedListSlot) {
      setMessage('Selecciona un slot antes de cambiar su estado deseado.');
      return;
    }
    desiredStateMutation.mutate({
      slotId: selectedListSlot.id,
      desiredWorkerState,
      desiredBotState,
      desiredReason
    });
  }

  function sendWorkerControl(action: AdminSlotAction, slotKey?: string) {
    if (workerOperationActive || workerControlMutation.isPending) {
      setMessage('Hay un comando del servidor en curso. Espera a que termine antes de lanzar otro.');
      return;
    }
    const agentId = selectedAgentId || workerControlHost?.agentId;
    if (!agentId) {
      setMessage('Selecciona un servidor antes de enviar comandos remotos.');
      return;
    }
    if ((action === 'admin_start_slot_worker' || action === 'admin_stop_slot_worker') && !slotKey) {
      setMessage('Selecciona un slot local antes de controlar un worker concreto.');
      return;
    }
    setWorkerOperation({
      action,
      agentId,
      slotKey,
      status: 'preparing',
      message: workerOperationMessage(action, 'preparing'),
      detail: slotKey ? `${agentId} / ${slotKey}` : agentId,
      startedAt: Date.now()
    });
    workerControlMutation.mutate({ action, agentId, slotKey });
  }

  const isEditing = Boolean(selectedListSlotId);
  const formTitle = isEditing
    ? `Editar: ${selectedListSlot?.slotKey || form.slotKey}`
    : 'Nuevo slot';
  const selectedHostLabel = selectedHost?.label || selectedAgentId || 'Sin servidor';
  const selectedHostState = selectedHost?.status || 'sin heartbeat';
  const selectedSlotHeadline = selectedListSlot
    ? `${selectedListSlot.slotKey} · ${selectedListSlot.userEmail || 'sin usuario'}`
    : form.slotKey
      ? `${form.slotKey} · ${form.userId ? 'usuario seleccionado' : 'sin usuario'}`
      : 'Selecciona un slot local';

  return (
    <main className="admin-shell admin-shell-redesign">
      {workerOperation && (
        <WorkerOperationModal
          operation={workerOperation}
          active={workerOperationActive || workerControlMutation.isPending}
          onClose={() => setWorkerOperation(null)}
        />
      )}
      <section className="admin-fleet-bar">
        <div className="admin-fleet-context">
          <span className="admin-eyebrow">Control multiserver</span>
          <div className="admin-server-row">
            <Server size={18} aria-hidden="true" />
            {agentHosts.length > 0 ? (
              <select
                className="admin-server-select"
                aria-label="Servidor"
                value={selectedAgentId || ''}
                onChange={(event) => selectAgent(event.target.value)}
              >
                {agentHosts.map((host) => {
                  const hostSlotCount = slots.filter((s) => s.agentId === host.agentId).length;
                  return (
                    <option key={host.agentId} value={host.agentId}>
                      {host.agentId} "{host.label}" · {host.status} · {hostSlotCount} slots
                    </option>
                  );
                })}
              </select>
            ) : (
              <strong>Sin servidores</strong>
            )}
          </div>
          <p>{pendingCustomerUsers.length} usuarios sin slot · {displayedFreeSlots} slots libres · capacidad {capacityLabel(displayedCapacityStatus)} · bridge {selectedHostState}</p>
        </div>
        <div className="admin-metrics-grid" aria-label="Resumen admin">
          <AdminMetric icon={<Bot size={16} aria-hidden="true" />} label="Slots activos" value={`${readySlots}/${slots.length}`} tone={readySlots ? 'ok' : 'warn'} />
          <AdminMetric icon={<Users size={16} aria-hidden="true" />} label="Pendientes" value={String(pendingCustomerUsers.length)} tone={pendingCustomerUsers.length ? 'warn' : 'ok'} />
          <AdminMetric icon={<HardDrive size={16} aria-hidden="true" />} label="Locales libres" value={String(displayedFreeSlots)} tone={displayedFreeSlots ? 'ok' : 'warn'} />
          <AdminMetric icon={<Activity size={16} aria-hidden="true" />} label="Bridge" value={selectedHostState} tone={selectedHost?.status === 'online' ? 'ok' : 'bad'} />
        </div>
        <div className="admin-fleet-actions">
          <button
            className="primary"
            disabled={!canAutoAssign || autoAssignMutation.isPending}
            title={canAutoAssign ? `Asignar ${nextAutoSlot?.slotKey} a ${nextAutoUser?.email}` : 'Necesita usuario pendiente y slot libre'}
            onClick={() => autoAssignMutation.mutate()}
          >
            <UserPlus size={16} aria-hidden="true" />
            Asignar siguiente
          </button>
          {message && <div className="ops-message">{message}</div>}
        </div>
      </section>
      {overviewQuery.error && <ErrorState error={overviewQuery.error} />}

      <section className="admin-grid">

        {/* COLUMNA 1: Lista de slots (filtrada por servidor seleccionado) */}
        <div className="admin-column">
          <AdminDisclosure
            title={`Slots — ${selectedHostLabel}`}
            meta={`${selectedAgentSlots.length} Supabase · ${selectedAgentLocalSlotOptions.length} locales`}
            icon={<Database size={16} aria-hidden="true" />}
          >
            <div className="section-label">
              Slots en Supabase
              <button
                className="admin-mini-button"
                onClick={() => {
                  setSelectedListSlotId(null);
                  setForm({ slotKey: '', userId: '', label: 'Hosted Test', agentId: form.agentId, regionId: '', licenseStatus: 'trial', licensePlan: 'trial', linked: false, enabledModules: defaultModules });
                  setSlotEditorOpen(true);
                  setMessage('');
                }}
              >
                + Nuevo
              </button>
            </div>
            {!selectedAgentSlots.length && <EmptyState>Sin slots asignados</EmptyState>}
            <div className="slot-list admin-scroll-list">
              {selectedAgentSlots.map((slot) => (
                <div
                  key={slot.id}
                  className={`slot-card admin-remote-slot-card ${selectedListSlotId === slot.id ? 'selected' : ''}`}
                >
                  <button className="slot-card-main" onClick={() => fillSlot(slot)}>
                    <strong>
                      {slot.slotKey} <span className={`badge badge-${slot.licenseStatus}`}>{licenseSummary(slot)}</span>
                      {assistanceSlotIds.has(slot.id) && <span className="badge badge-assistance">asistencia</span>}
                    </strong>
                    <span>{slot.label}</span>
                    <span>{slot.userEmail || 'sin usuario'} · {slot.linked ? 'linked' : 'sin vincular'} · {slot.state}</span>
                    <span>
                      deseado: worker {slot.desiredWorkerState || 'running'} · bot {slot.desiredBotState || 'idle'}
                      {slot.desiredReason ? ` · ${slot.desiredReason}` : ''}
                    </span>
                  </button>
                </div>
              ))}
            </div>

            <div className="section-label" style={{ marginTop: '12px' }}>Slots locales (BlueStacks)</div>
            {!selectedAgentLocalSlotOptions.length && <EmptyState>Sin inventario local reciente</EmptyState>}
            <div className="slot-list admin-scroll-list">
              {selectedAgentLocalSlotOptions.map((option) => {
                const isAssigned = Boolean(selectedAgentSlots.find((slot) => slot.slotKey === option.slotKey));
                return (
                  <LocalSlotCard
                    assigned={isAssigned}
                    key={`${option.agentId}-${option.slotKey}`}
                    option={option}
                    selected={form.slotKey === option.slotKey && form.agentId === option.agentId}
                    onSelect={() => fillLocalSlot(option)}
                    onDelete={!isAssigned ? () => {
                      const confirmed = window.confirm(`Eliminar slot local ${option.slotKey}? Esta accion no se puede deshacer.`);
                      if (confirmed) deleteLocalSlotMutation.mutate({ slot_id: option.slotKey, agent_id: option.agentId });
                    } : undefined}
                  />
                );
              })}
            </div>
          </AdminDisclosure>

          {Boolean(api.seedRegion) && <RegionSeedPanel api={api} />}
        </div>

        {/* COLUMNA 2: Formulario contextual */}
        <div className="admin-column admin-primary">
          <AdminDisclosure
            title="Registro de eventos"
            meta={auditEvents.length
              ? (auditTotal == null ? `${auditEvents.length} eventos` : `${auditOffset + 1}-${auditOffset + auditEvents.length} de ${auditTotal}`)
              : (auditTotal ? `0 de ${auditTotal}` : '0 eventos')}
            icon={<Activity size={16} aria-hidden="true" />}
            className="admin-audit-disclosure"
          >
            <AuditConsole
              events={auditEvents}
              selectedSlotId={auditSlotId}
              slots={selectedAgentSlots}
              users={users}
              page={auditPage}
              pageSize={auditLimit || auditPageSize}
              total={auditTotal}
              hasMore={auditHasMore}
              isLoading={auditQuery.isFetching}
              error={auditQuery.error}
              onSelectedSlotChange={(slotId) => {
                setAuditSlotId(slotId);
                setAuditPage(0);
              }}
              onNextPage={() => setAuditPage((page) => page + 1)}
              onPreviousPage={() => setAuditPage((page) => Math.max(0, page - 1))}
              onRefresh={() => {
                void queryClient.invalidateQueries({ queryKey: ['admin-audit-events'] });
              }}
            />
          </AdminDisclosure>

          {Boolean(api.getActiveLinkingSession && api.sendLinkingChatMessage) && (
            <AdminDisclosure
              key={selectedListSlot?.id || 'none'}
              title="Asistencia de vinculacion"
              meta={selectedListSlot ? `${selectedListSlot.slotKey} · ${selectedListSlot.userEmail || 'sin usuario'}` : 'Selecciona un slot'}
              icon={<Link2 size={16} aria-hidden="true" />}
              defaultOpen={Boolean(selectedListSlot && assistanceSlotIds.has(selectedListSlot.id))}
            >
              <AdminLinkingAssistancePanel api={api} slot={selectedListSlot} />
            </AdminDisclosure>
          )}

          <AdminDisclosure
            title={formTitle}
            meta={isEditing ? 'Editar asignacion y estado' : 'Crear o reservar slot'}
            icon={<UserPlus size={16} aria-hidden="true" />}
            open={slotEditorOpen}
            onOpenChange={setSlotEditorOpen}
            className="admin-slot-editor-disclosure"
          >
            <div className="admin-selected-strip">
              <div>
                <span>Slot</span>
                <strong>{selectedSlotHeadline}</strong>
              </div>
              <div>
                <span>Servidor</span>
                <strong>{selectedHostLabel}</strong>
              </div>
              <div>
                <span>Estado</span>
                <strong className={`admin-status-text ${provisioning.status}`}>{provisioning.label}</strong>
              </div>
            </div>
            <div className="admin-form-grid">
            <label>
              Slot local
              <select value={form.slotKey} onChange={(event) => setSlotKey(event.target.value)}>
                <option value="">Selecciona slot</option>
                {commandSlotKeys.map((slotKey) => (
                  <option key={slotKey} value={slotKey}>{slotKey}</option>
                ))}
              </select>
            </label>
            <label>
              Usuario (opcional)
              <select value={form.userId || ''} onChange={(event) => {
                const user = users.find((item) => item.id === event.target.value);
                if (user) fillUser(user);
                else setForm({ ...form, userId: '' });
              }}>
                <option value="">Sin usuario (reservar)</option>
                {orderedUsers.map((user) => {
                  const userSlots = slots.filter((slot) => slot.userEmail === user.email);
                  return (
                    <option key={user.id} value={user.id}>
                      {user.email} {userSlots.length ? `(${userSlots.map((s) => s.slotKey).join(', ')})` : '(sin slot)'}
                    </option>
                  );
                })}
              </select>
            </label>
            <label>
              Etiqueta
              <input value={form.label} onChange={(event) => setForm({ ...form, label: event.target.value })} />
            </label>
            <label>
              Agente
              <select
                value={form.agentId}
                onChange={(event) => {
                  const agentId = event.target.value;
                  selectAgent(agentId);
                }}
              >
                {agentHosts.map((host) => (
                  <option key={host.agentId} value={host.agentId}>
                    {host.label} ({host.agentId})
                  </option>
                ))}
                {!agentHosts.some((h) => h.agentId === form.agentId) && form.agentId && (
                  <option value={form.agentId}>{form.agentId}</option>
                )}
              </select>
            </label>
            <label>
              Region Intelligence
              <input
                value={form.regionId || ''}
                inputMode="numeric"
                onChange={(event) => setForm({ ...form, regionId: event.target.value })}
              />
            </label>
            <label>
              Licencia
              <select
                value={form.licensePlan || licensePlanFromStatus(form.licenseStatus)}
                onChange={(event) => {
                  const licensePlan = event.target.value as LicensePlan;
                  setForm({ ...form, licensePlan, licenseStatus: licenseStatusFromPlan(licensePlan) });
                }}
              >
                <option value="trial">trial</option>
                <option value="pro">pro</option>
                <option value="pro_plus">pro+</option>
              </select>
            </label>
            </div>
            <label className="check">
              <input type="checkbox" checked={form.linked} onChange={(event) => setForm({ ...form, linked: event.target.checked })} />
              Vinculado a cuenta del juego
            </label>
            <details className="admin-inline-disclosure">
              <summary>Modulos</summary>
              <div className="checks">
                <label className="check">
                  <input checked={form.enabledModules.collect} type="checkbox" onChange={(event) => setModule('collect', event.target.checked)} />
                  Recolectar
                </label>
                <label className="check">
                  <input checked={form.enabledModules.train_troops} type="checkbox" onChange={(event) => setModule('train_troops', event.target.checked)} />
                  Entrenar tropas
                </label>
                <label className="check">
                  <input checked={form.enabledModules.auto_hunt} type="checkbox" onChange={(event) => setModule('auto_hunt', event.target.checked)} />
                  Auto Hunt
                </label>
                <label className="check">
                  <input checked={form.enabledModules.intelligence} type="checkbox" onChange={(event) => setModule('intelligence', event.target.checked)} />
                  Intelligence
                </label>
              </div>
            </details>

            {selectedLocalSlotOption && (
              <LocalSlotInlineSummary option={selectedLocalSlotOption} assigned={Boolean(selectedFormSlot)} />
            )}
            {provisioning.status !== 'ready' && form.slotKey && (
              <div className="notice warning">Provisioning: {provisioning.label}</div>
            )}

            <div className="action-pair">
              <button
                className="primary"
                disabled={saveMutation.isPending || !canSaveAssignment}
                onClick={() => saveMutation.mutate()}
              >
                <Save size={16} aria-hidden="true" />
                {isEditing ? 'Guardar cambios' : 'Crear slot'}
              </button>
              {isEditing && (
                <button
                  disabled={!selectedListSlot}
                  onClick={() => selectedListSlot && onOpenDashboardSlot?.(selectedListSlot.id)}
                >
                  <ExternalLink size={16} aria-hidden="true" />
                  Ver dashboard
                </button>
              )}
            </div>
            {isEditing && (
              <>
                <div className="section-label">Estado deseado</div>
                <div className="notice">
                  worker {selectedListSlot?.desiredWorkerState || 'running'} · bot {selectedListSlot?.desiredBotState || 'idle'}
                  {selectedListSlot?.desiredReason ? ` · ${selectedListSlot.desiredReason}` : ''}
                </div>
                {api.updateSlotDesiredState && (
                  <div className="action-pair">
                    <button
                      type="button"
                      disabled={desiredStateMutation.isPending}
                      onClick={() => setDesiredForSelected('running', 'idle', 'admin_allow')}
                    >
                      <Play size={16} aria-hidden="true" />
                      Permitir operar
                    </button>
                    <button
                      type="button"
                      className="danger"
                      disabled={desiredStateMutation.isPending}
                      onClick={() => setDesiredForSelected('stopped', 'stopped', 'admin_stop')}
                    >
                      <Square size={16} aria-hidden="true" />
                      Parar slot
                    </button>
                  </div>
                )}
              </>
            )}
            {isEditing && (
              <div className="action-pair">
                <button type="button" onClick={reserveSlot}>
                  <AlertTriangle size={16} aria-hidden="true" />
                  Marcar sin vincular
                </button>
                <button type="button" onClick={markLinked}>
                  <Link2 size={16} aria-hidden="true" />
                  Marcar vinculado
                </button>
              </div>
            )}
            {isEditing && (
              <details className="admin-inline-disclosure admin-danger-zone">
                <summary>Danger zone</summary>
                <button
                  className="danger"
                  disabled={!selectedListSlot || releaseMutation.isPending}
                  onClick={releaseSelectedSlot}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  Eliminar slot de Supabase
                </button>
              </details>
            )}
          </AdminDisclosure>

          {selectedHost && (
            <AdminDisclosure
              title="Provisioning local"
              meta={`${provisioning.label} · ${formatAge(selectedHost.inventoryUpdatedAt)}`}
              icon={<HardDrive size={16} aria-hidden="true" />}
            >
              <div className="slot-list">
                <div className="slot-card static-card">
                  <strong>{selectedHost.label} - {selectedHost.status}</strong>
                  <span>agent: {selectedHost.agentId} · heartbeat: {formatAge(selectedHost.lastSeenAt)}</span>
                </div>
                <HostCapacityPanel
                  capacity={selectedHost.inventory?.capacity}
                  localFreeCount={availableLocalSlots.length}
                  disabled={!workerControlAvailable || workerControlMutation.isPending || workerOperationActive}
                  onReconcile={() => sendWorkerControl('admin_reconcile_host_capacity')}
                />
                <ProvisioningChecklist result={provisioning} />
              </div>
            </AdminDisclosure>
          )}

          <AdminDisclosure
            title="Control workers hosted"
            meta={workerControlHost ? `${workerControlHost.agentId} · ${workerControlHost.status}` : 'sin servidor'}
            icon={<Activity size={16} aria-hidden="true" />}
            className="admin-worker-control-disclosure"
          >
            {/* El servidor se toma de selectedAgentId (selector global en ops-bar) */}
            <div className="notice">
              Servidor activo: <strong>{workerControlHost?.label || selectedAgentId || 'Sin servidor'}</strong>
              {workerControlHost && ` (${workerControlHost.agentId} · ${workerControlHost.status})`}
            </div>
            <div className="action-pair">
              <button
                type="button"
                disabled={!workerControlAvailable || !workerControlHost || workerControlMutation.isPending || workerOperationActive}
                onClick={() => sendWorkerControl('admin_start_hosted_workers')}
              >
                <Play size={16} aria-hidden="true" />
                Arrancar todos
              </button>
              <button
                type="button"
                className="danger"
                disabled={!workerControlAvailable || !workerControlHost || workerControlMutation.isPending || workerOperationActive}
                onClick={() => sendWorkerControl('admin_stop_hosted_workers')}
              >
                <Square size={16} aria-hidden="true" />
                Parar todos
              </button>
            </div>
            <label>
              Worker concreto
              <select
                value={workerControlSlotKey}
                onChange={(event) => setWorkerControlSlotKey(event.target.value)}
              >
                {!workerControlSlots.length && <option value="">Sin slots locales</option>}
                {workerControlSlots.map((slot) => (
                  <option key={`${slot.agentId}:${slot.slotKey}`} value={slot.slotKey}>
                    {slot.slotKey} - {slot.health}
                  </option>
                ))}
              </select>
            </label>
            <div className="action-pair">
              <button
                type="button"
                disabled={!workerControlAvailable || !workerControlSlotKey || workerControlMutation.isPending || workerOperationActive}
                onClick={() => sendWorkerControl('admin_start_slot_worker', workerControlSlotKey)}
              >
                <Play size={16} aria-hidden="true" />
                Arrancar worker
              </button>
              <button
                type="button"
                className="danger"
                disabled={!workerControlAvailable || !workerControlSlotKey || workerControlMutation.isPending || workerOperationActive}
                onClick={() => sendWorkerControl('admin_stop_slot_worker', workerControlSlotKey)}
              >
                <Square size={16} aria-hidden="true" />
                Parar worker
              </button>
            </div>
          </AdminDisclosure>
        </div>

        {/* COLUMNA 3: Usuarios + Auditoría */}
        <div className="admin-column">
          <AdminDisclosure
            title="Usuarios"
            meta={`${users.length} usuarios · ${pendingUsers.length} sin slot`}
            icon={<Users size={16} aria-hidden="true" />}
          >
            <div className="summary-strip">
              <span>{users.length} usuarios</span>
              <span>{pendingUsers.length} sin slot</span>
            </div>
            {!users.length && <EmptyState>Sin usuarios</EmptyState>}
            <div className="slot-list admin-scroll-list compact">
              {orderedUsers.map((user) => {
                const userSlots = slots.filter((slot) => slot.userEmail === user.email);
                return (
                  <button
                    key={user.id}
                    className={`slot-card ${form.userId === user.id ? 'selected' : ''}`}
                    onClick={() => fillUser(user)}
                  >
                    <strong>{user.email}</strong>
                    <span>{user.role} · {userSlots.length ? userSlots.map((s) => s.slotKey).join(', ') : 'sin slot'}</span>
                  </button>
                );
              })}
            </div>
          </AdminDisclosure>

          {Boolean(api.listWaitlist) && <WaitlistPanel api={api} />}
        </div>

      </section>
    </main>
  );
}


function RegionSeedPanel({ api }: { api: BotApi }) {
  const queryClient = useQueryClient();
  const [regionId, setRegionId] = useState('');
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const summariesQuery = useQuery({
    queryKey: ['admin-region-summaries'],
    queryFn: () => api.listRegionSummaries!(),
    enabled: Boolean(api.listRegionSummaries),
    refetchInterval: 20000
  });

  const seedMutation = useMutation({
    mutationFn: () => api.seedRegion!(regionId.trim()),
    onSuccess: (result) => {
      setFeedback({
        tone: 'ok',
        text: `Region ${result.regionId} sembrada: ${result.totalCells.toLocaleString()} celdas`
          + ` (${result.inserted.toLocaleString()} nuevas, plantilla ${result.templateRegionId ?? '—'})`
      });
      setRegionId('');
      void queryClient.invalidateQueries({ queryKey: ['admin-region-summaries'] });
    },
    onError: (error) => {
      setFeedback({ tone: 'bad', text: error instanceof Error ? error.message : 'Error al sembrar la region' });
    }
  });

  const regions = summariesQuery.data ?? [];
  const trimmed = regionId.trim();
  const isNumeric = /^\d+$/.test(trimmed);
  const alreadySeeded = regions.some((region) => region.regionId === trimmed && region.cellCount > 0);
  const canSeed = isNumeric && !seedMutation.isPending;

  return (
    <AdminDisclosure
      title="Regiones de Intelligence"
      meta={`${regions.length} sembradas`}
      icon={<MapIcon size={16} aria-hidden="true" />}
    >
      <div className="section-label">Sembrar region</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
        <input
          value={regionId}
          inputMode="numeric"
          placeholder="ej. 4394"
          style={{ flex: 1 }}
          onChange={(event) => { setRegionId(event.target.value); setFeedback(null); }}
          onKeyDown={(event) => { if (event.key === 'Enter' && canSeed) seedMutation.mutate(); }}
        />
        <button className="admin-mini-button" disabled={!canSeed} onClick={() => seedMutation.mutate()}>
          {seedMutation.isPending ? 'Sembrando…' : 'Sembrar'}
        </button>
      </div>
      {trimmed && !isNumeric && (
        <p style={{ fontSize: '0.78rem', opacity: 0.7, margin: '6px 0 0' }}>
          El id de region debe ser numerico (ej. 4394).
        </p>
      )}
      {alreadySeeded && !seedMutation.isPending && (
        <p style={{ fontSize: '0.78rem', opacity: 0.7, margin: '6px 0 0' }}>
          La region {trimmed} ya esta sembrada. Volver a sembrar es seguro (idempotente).
        </p>
      )}
      {feedback && (
        <p style={{
          fontSize: '0.82rem',
          margin: '8px 0 0',
          color: feedback.tone === 'ok' ? 'var(--ok, #2e7d32)' : 'var(--bad, #c62828)'
        }}>
          {feedback.text}
        </p>
      )}
      <div className="section-label" style={{ marginTop: '12px' }}>Regiones sembradas</div>
      {summariesQuery.isLoading && <EmptyState>Cargando…</EmptyState>}
      {summariesQuery.error && <EmptyState>Error al cargar regiones</EmptyState>}
      {!summariesQuery.isLoading && !summariesQuery.error && !regions.length && (
        <EmptyState>Ninguna region sembrada</EmptyState>
      )}
      <div className="slot-list admin-scroll-list compact">
        {regions.map((region) => (
          <div key={region.regionId} className="slot-card">
            <strong>Region {region.regionId}</strong>
            <span>{region.cellCount.toLocaleString()} celdas · {region.scannedCount.toLocaleString()} escaneadas</span>
            <span>{region.lastScanAt ? `ultimo scan ${new Date(region.lastScanAt).toLocaleString()}` : 'sin escanear aun'}</span>
          </div>
        ))}
      </div>
    </AdminDisclosure>
  );
}

function WaitlistPanel({ api }: { api: BotApi }) {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const waitlistQuery = useQuery({
    queryKey: ['admin-waitlist'],
    queryFn: () => api.listWaitlist!(),
    enabled: Boolean(api.listWaitlist),
    refetchInterval: 15000
  });

  const promoteMutation = useMutation({
    mutationFn: (id: string) => api.promoteWaitlistEntry!(id),
    onSuccess: () => {
      setFeedback({ tone: 'ok', text: 'Invitación marcada. Se enviará el email en el próximo ciclo (≤5 min).' });
      void queryClient.invalidateQueries({ queryKey: ['admin-waitlist'] });
    },
    onError: (error) => {
      setFeedback({ tone: 'bad', text: error instanceof Error ? error.message : 'No se pudo invitar' });
    }
  });

  const entries = waitlistQuery.data ?? [];
  const waiting = entries.filter((e) => e.status === 'waiting');
  const invited = entries.filter((e) => e.status === 'invited');
  const claimed = entries.filter((e) => e.status === 'claimed');

  const statusLabel: Record<string, string> = {
    waiting: 'en cola',
    invited: 'invitado',
    claimed: 'con acceso',
    expired: 'caducado',
    cancelled: 'cancelado'
  };

  return (
    <AdminDisclosure
      title="Lista de espera"
      meta={`${waiting.length} en cola · ${invited.length} invitados · ${claimed.length} con acceso`}
      icon={<Clock size={16} aria-hidden="true" />}
    >
      <div className="summary-strip">
        <span>{waiting.length} en cola</span>
        <span>{invited.length} invitados</span>
        <span>{claimed.length} con acceso</span>
      </div>
      {feedback && (
        <p style={{
          fontSize: '0.82rem',
          margin: '8px 0 0',
          color: feedback.tone === 'ok' ? 'var(--ok, #2e7d32)' : 'var(--bad, #c62828)'
        }}>
          {feedback.text}
        </p>
      )}
      {waitlistQuery.isLoading && <EmptyState>Cargando…</EmptyState>}
      {waitlistQuery.error && <EmptyState>Error al cargar la lista</EmptyState>}
      {!waitlistQuery.isLoading && !waitlistQuery.error && !entries.length && (
        <EmptyState>Nadie en lista de espera</EmptyState>
      )}
      <div className="slot-list admin-scroll-list compact">
        {entries.map((entry, index) => {
          const position = entry.status === 'waiting'
            ? waiting.findIndex((e) => e.id === entry.id) + 1
            : null;
          return (
            <div key={entry.id} className="slot-card">
              <strong>
                {position ? `#${position} · ` : ''}{entry.email}{' '}
                <span className={`badge badge-${entry.status === 'claimed' ? 'active' : entry.status === 'invited' ? 'trial' : 'expired'}`}>
                  {statusLabel[entry.status] ?? entry.status}
                </span>
              </strong>
              <span>
                {new Date(entry.createdAt).toLocaleDateString()}
                {entry.invitedAt ? ` · invitado ${new Date(entry.invitedAt).toLocaleDateString()}` : ''}
                {entry.status === 'invited' && !entry.inviteEmailSentAt ? ' · email pendiente' : ''}
                {entry.status === 'invited' && entry.inviteEmailSentAt ? ' · email enviado' : ''}
              </span>
              {entry.status === 'waiting' && (
                <button
                  className="admin-mini-button"
                  disabled={promoteMutation.isPending}
                  onClick={() => promoteMutation.mutate(entry.id)}
                  style={{ marginTop: '4px', alignSelf: 'flex-start' }}
                  data-testid={`waitlist-invite-${index}`}
                >
                  <UserPlus size={14} aria-hidden="true" /> Invitar ahora
                </button>
              )}
            </div>
          );
        })}
      </div>
    </AdminDisclosure>
  );
}

function AdminMetric({
  icon,
  label,
  value,
  tone = 'neutral'
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'bad';
}) {
  return (
    <div className={`admin-metric admin-metric-${tone}`}>
      <span>{icon}</span>
      <div>
        <strong>{value}</strong>
        <em>{label}</em>
        <span className="sr-only">{value} {label.toLowerCase()}</span>
      </div>
    </div>
  );
}

function AdminDisclosure({
  title,
  meta,
  icon,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  className
}: {
  title: string;
  meta?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = open ?? internalOpen;
  return (
    <details
      className={`admin-disclosure${className ? ` ${className}` : ''}`}
      open={isOpen}
      onToggle={(event) => {
        const nextOpen = event.currentTarget.open;
        if (open === undefined) setInternalOpen(nextOpen);
        onOpenChange?.(nextOpen);
      }}
    >
      <summary>
        <span className="admin-disclosure-title">
          <span className="admin-disclosure-chevron" aria-hidden="true" />
          {icon}
          <strong>{title}</strong>
        </span>
        {meta && <span className="admin-disclosure-meta">{meta}</span>}
      </summary>
      <div className="admin-disclosure-body">{children}</div>
    </details>
  );
}

function AdminLinkingAssistancePanel({ api, slot }: { api: BotApi; slot: BotSlot | null }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const sessionQuery = useQuery({
    queryKey: ['admin-linking-session', slot?.id],
    queryFn: () => api.getActiveLinkingSession!(slot!.id),
    enabled: Boolean(slot && api.getActiveLinkingSession),
    refetchInterval: 2000
  });
  const session = sessionQuery.data || null;
  const sessionId = session?.id || null;
  const chatQuery = useQuery({
    queryKey: ['admin-linking-chat', sessionId],
    queryFn: () => api.getLinkingChatMessages!(sessionId!),
    enabled: Boolean(sessionId && api.getLinkingChatMessages),
    refetchInterval: 3000
  });
  const messages = chatQuery.data || [];

  useEffect(() => {
    if (!sessionId || !api.subscribeLinkingChat) return;
    const unsubscribe = api.subscribeLinkingChat(sessionId, (incoming) => {
      queryClient.setQueryData<LinkingChatMessage[]>(['admin-linking-chat', sessionId], (prev) => {
        const list = prev || [];
        if (list.some((existing) => existing.id === incoming.id)) return list;
        return [...list, incoming];
      });
    });
    return unsubscribe;
  }, [sessionId, api, queryClient]);

  const sendMutation = useMutation({
    mutationFn: (body: string) => api.sendLinkingChatMessage!(sessionId!, body),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['admin-linking-chat', sessionId] });
    }
  });

  const handoffMutation = useMutation({
    mutationFn: (active: boolean) => api.setLinkingOperatorActive!(sessionId!, active),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-linking-session', slot?.id] });
      void queryClient.invalidateQueries({ queryKey: ['admin-linking-assistance'] });
    }
  });

  if (!slot) return <EmptyState>Selecciona un slot para ver su asistencia.</EmptyState>;
  if (!session) return <EmptyState>Sin sesion de vinculacion activa en este slot.</EmptyState>;

  const operatorActive = Boolean(session.operatorActive);
  const submit = () => {
    const body = draft.trim();
    if (!body || !sessionId) return;
    sendMutation.mutate(body);
  };

  return (
    <div className="admin-assistance">
      <div className="admin-assistance-status">
        <span>{session.assistanceRequestedAt ? 'Asistencia solicitada por el usuario' : 'Sesion activa'}</span>
        <button
          type="button"
          className={operatorActive ? 'warning-action' : 'primary'}
          disabled={handoffMutation.isPending}
          onClick={() => handoffMutation.mutate(!operatorActive)}
        >
          {operatorActive ? 'Devolver control al usuario' : 'Tomar control'}
        </button>
      </div>
      <div className="linking-chat-thread">
        {messages.length === 0 ? (
          <div className="linking-chat-empty">Aun no hay mensajes.</div>
        ) : (
          messages.map((entry) => (
            <div
              key={entry.id}
              className={`linking-chat-message ${entry.senderRole === 'operator' ? 'from-operator' : 'from-user'}`}
            >
              <span className="linking-chat-sender">{entry.senderRole === 'operator' ? 'Operador' : 'Usuario'}</span>
              <span className="linking-chat-body">{entry.body}</span>
            </div>
          ))
        )}
      </div>
      <div className="linking-input-row">
        <input
          type="text"
          value={draft}
          placeholder="Escribe un mensaje para el usuario"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
        <button type="button" disabled={sendMutation.isPending || !draft.trim()} onClick={submit}>
          <Send size={16} aria-hidden="true" />
          <span>Enviar</span>
        </button>
      </div>
    </div>
  );
}

function licenseSummary(slot: BotSlot): string {
  if (slot.licenseStatus === 'active') return licenseLabel(licensePlanFromSlot(slot));
  if (slot.licenseStatus === 'expired' && licensePlanFromSlot(slot) === 'trial') return 'trial expired';
  if (slot.licenseStatus !== 'trial') return slot.licenseStatus;
  if (!slot.trialEndsAt) return 'trial';
  const expiresAt = new Date(slot.trialEndsAt);
  if (Number.isNaN(expiresAt.getTime())) return 'trial';
  const remainingMs = expiresAt.getTime() - Date.now();
  if (remainingMs <= 0) return 'trial expired';
  const days = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  return `trial ${days}d`;
}

function normalizeSaveSlotRequest(form: SaveSlotRequest, currentSlot?: BotSlot | null): SaveSlotRequest {
  const licensePlan = form.licensePlan || licensePlanFromStatus(form.licenseStatus);
  const regionId = String(form.regionId || '').trim();
  const currentRegionId = String(currentSlot?.regionId || '').trim();
  return {
    ...form,
    regionId: regionId && regionId !== currentRegionId ? regionId : undefined,
    licensePlan,
    licenseStatus: licenseStatusFromPlan(licensePlan)
  };
}

function licenseStatusFromPlan(plan: LicensePlan): SaveSlotRequest['licenseStatus'] {
  return plan === 'trial' ? 'trial' : 'active';
}

function licensePlanFromStatus(status: SaveSlotRequest['licenseStatus']): LicensePlan {
  return status === 'trial' ? 'trial' : 'pro';
}

function licensePlanFromSlot(slot: BotSlot): LicensePlan {
  if (slot.licensePlan) return slot.licensePlan;
  const planKey = String(slot.planKey || '').toLowerCase();
  if (slot.licenseStatus === 'trial' || planKey === 'trial') return 'trial';
  return isProPlusPlan(planKey) ? 'pro_plus' : 'pro';
}

function licenseLabel(plan: LicensePlan): string {
  return plan === 'pro_plus' ? 'pro+' : plan;
}

function isProPlusPlan(planKey: string): boolean {
  return ['pro_plus', 'pro+', 'pro-plus', 'proplus'].includes(planKey);
}

function LocalSlotCard({
  option,
  assigned,
  selected,
  onSelect,
  onDelete
}: {
  option: LocalSlotOption;
  assigned: boolean;
  selected: boolean;
  onSelect: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className={`slot-card ${selected ? 'selected' : ''}`}>
      <button className="slot-card-main" onClick={onSelect} style={{ all: 'unset', display: 'contents', cursor: 'pointer' }}>
        <strong>
          {option.slotKey} - {option.profile} - {option.health}
        </strong>
        <span>
          {option.instanceName || '-'} / {option.instanceKey || '-'} / {option.deviceId || '-'}
        </span>
        <span>{assigned ? 'asignado en Supabase' : 'disponible para asignar'}</span>
        {option.warnings.length ? <span>warnings: {option.warnings.join(' | ')}</span> : <span>sin warnings</span>}
      </button>
      {!assigned && onDelete && (
        <button
          className="danger"
          style={{ marginTop: '4px', fontSize: '0.75rem', padding: '2px 8px' }}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          Eliminar local
        </button>
      )}
    </div>
  );
}

function LocalSlotInlineSummary({ option, assigned }: { option: LocalSlotOption; assigned: boolean }) {
  return (
    <div className={`notice ${option.health === 'ready' ? '' : 'warning'}`}>
      Slot local seleccionado: {option.slotKey} ({option.profile}, {option.health}).{' '}
      {assigned ? 'Ya existe como slot remoto.' : 'Al guardar se creara o actualizara su asignacion remota.'}
    </div>
  );
}

function AuditConsole({
  events,
  selectedSlotId,
  slots,
  users,
  page,
  pageSize,
  total,
  hasMore,
  isLoading,
  error,
  onSelectedSlotChange,
  onNextPage,
  onPreviousPage,
  onRefresh
}: {
  events: AuditEvent[];
  selectedSlotId: string;
  slots: BotSlot[];
  users: UserProfile[];
  page: number;
  pageSize: number;
  total?: number | null;
  hasMore: boolean;
  isLoading: boolean;
  error: unknown;
  onSelectedSlotChange: (slotId: string) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onRefresh: () => void;
}) {
  const selectedSlot = selectedSlotId === 'all' ? null : slots.find((s) => s.id === selectedSlotId);
  const filteredEvents =
    selectedSlotId === 'all'
      ? events
      : events.filter(
          (event) =>
            event.slotId === selectedSlotId &&
            (!selectedSlot?.agentId || event.agentId === selectedSlot.agentId)
        );
  const slotById = new Map(slots.map((slot) => [slot.id, slot]));
  const userById = new Map(users.map((user) => [user.id, user]));
  const start = events.length ? page * pageSize + 1 : 0;
  const end = page * pageSize + events.length;
  const totalLabel = typeof total === 'number' ? String(total) : '...';
  const rangeLabel = selectedSlotId === 'all'
    ? `${start}-${end} de ${totalLabel}`
    : `${filteredEvents.length}/${events.length} en pagina`;

  return (
    <div className="audit-panel">
      <div className="audit-toolbar">
        <select
          aria-label="Filtrar registro por slot"
          value={selectedSlotId}
          onChange={(event) => onSelectedSlotChange(event.target.value)}
        >
          <option value="all">Todos los slots del servidor</option>
          {slots.map((slot) => (
            <option key={slot.id} value={slot.id}>
              {slot.slotKey} - {slot.label}
            </option>
          ))}
        </select>
        <span>
          {isLoading ? 'Cargando...' : rangeLabel}
        </span>
      </div>
      <div className="audit-pagination">
        <button type="button" onClick={onPreviousPage} disabled={page === 0 || isLoading}>
          Anteriores
        </button>
        <button type="button" onClick={onRefresh} disabled={isLoading}>
          Actualizar
        </button>
        <button type="button" onClick={onNextPage} disabled={!hasMore || isLoading}>
          Siguientes
        </button>
      </div>
      {Boolean(error) && (
        <div className="notice warning">
          No se pudo cargar el registro: {error instanceof Error ? error.message : String(error)}
        </div>
      )}
      <pre className="audit-console" aria-label="Registro de eventos del bot">
        {filteredEvents.length
          ? filteredEvents
              .map((event) => formatAuditLine(event, slotById.get(String(event.slotId || '')), userById.get(String(event.userId || ''))))
              .join('\n')
          : 'Sin eventos recientes'}
      </pre>
    </div>
  );
}

function formatAuditLine(event: AuditEvent, slot?: BotSlot, _user?: UserProfile): string {
  const time = formatDateTimeForLog(event.createdAt);
  const slotLabel = slot ? slot.slotKey : event.slotId ? `slot:${shortId(event.slotId)}` : 'slot:-';
  const payload = compactPayload(event.eventType, event.payload);
  return `${time} ${slotLabel} ${event.eventType}${payload ? ` ${payload}` : ''}`;
}

function compactPayload(eventType: string, payload?: Record<string, unknown>): string {
  if (!payload || !Object.keys(payload).length) return '';
  const details: string[] = [];
  const action = payload.action ? stringifyAuditValue(payload.action) : '';
  if (action && eventType.startsWith('command_')) {
    details.push(action);
  }
  if (payload.reason) details.push(`reason="${stringifyAuditValue(payload.reason)}"`);
  if (payload.error) details.push(`error="${stringifyAuditValue(payload.error)}"`);
  if (payload.message) details.push(`message="${stringifyAuditValue(payload.message)}"`);
  if (payload.license_status) details.push(`license=${stringifyAuditValue(payload.license_status)}`);
  if (typeof payload.linked === 'boolean') details.push(`linked=${String(payload.linked)}`);
  if (payload.state && ['slot_updated', 'slot_assigned', 'slot_released', 'link_completed', 'link_removed'].includes(eventType)) {
    details.push(`state=${stringifyAuditValue(payload.state)}`);
  }
  if (details.length) return details.join(' ');

  const priorityKeys = ['status', 'result', 'last_result'];
  const parts: string[] = [];
  for (const key of priorityKeys) {
    if (!(key in payload)) continue;
    parts.push(`${key}=${stringifyAuditValue(payload[key])}`);
  }
  return parts.join(' ');
}

function stringifyAuditValue(value: unknown): string {
  if (value == null) return '-';
  if (typeof value === 'string') return value.replace(/\s+/g, ' ').slice(0, 240);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value).replace(/\s+/g, ' ').slice(0, 240);
  } catch {
    return String(value).slice(0, 240);
  }
}

function HostCapacityPanel({
  capacity,
  localFreeCount,
  disabled,
  onReconcile
}: {
  capacity?: HostCapacitySnapshot;
  localFreeCount: number;
  disabled: boolean;
  onReconcile: () => void;
}) {
  const status = capacity?.status || 'unknown';
  const freeCount = Number(capacity?.free_count ?? localFreeCount);
  const assignedCount = Number(capacity?.assigned_count ?? 0);
  const shortfall = Number(capacity?.shortfall ?? 0);
  const nextSlotKey = capacity?.next_slot_key || 'slot_N';
  const freeSlots = capacity?.free_prepared_slot_keys?.length
    ? capacity.free_prepared_slot_keys.join(', ')
    : freeCount
      ? `${freeCount} preparados`
      : 'sin libres';
  return (
    <div className={`slot-card static-card provisioning-${status === 'ready' ? 'ready' : status === 'needs_capacity' ? 'warning' : 'blocked'}`}>
      <strong>Capacidad multiserver - {capacityLabel(status)}</strong>
      <span>{freeCount} libres · {assignedCount} asignados · deficit {shortfall}</span>
      <span>Libres: {freeSlots}</span>
      {status !== 'ready' && <span>Siguiente recomendado: {nextSlotKey}</span>}
      {capacity?.operator_action && <span>{capacity.operator_action}</span>}
      <button type="button" disabled={disabled} onClick={onReconcile}>
        <RefreshCw size={16} aria-hidden="true" />
        Reconciliar capacidad
      </button>
    </div>
  );
}

function ProvisioningChecklist({ result }: { result: ProvisioningResult }) {
  return (
    <div className={`slot-card static-card provisioning-${result.status}`}>
      <strong>Checklist de alta - {result.label}</strong>
      {result.items.map((item) => (
        <span key={item.label}>
          {item.ok ? 'OK' : 'Pendiente'} - {item.label}
          {item.detail ? `: ${item.detail}` : ''}
        </span>
      ))}
    </div>
  );
}

function WorkerOperationModal({
  operation,
  active,
  onClose
}: {
  operation: WorkerOperationState;
  active: boolean;
  onClose: () => void;
}) {
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - operation.startedAt) / 1000));
  return (
    <OperationModal
      active={active}
      tone={operation.error || operation.status === 'failed' || operation.status === 'timeout' ? 'error' : active ? 'working' : 'success'}
      ariaLabel="Operacion de workers"
      eyebrow={workerOperationTitle(operation.action)}
      title={operation.message}
      body={operation.slotKey ? `${operation.agentId} / ${operation.slotKey}` : operation.agentId}
      statusItems={[
        { label: 'Estado', value: operation.status },
        { label: 'Tiempo', value: `${elapsedSeconds}s` }
      ]}
      code={operation.commandId}
      detail={operation.detail}
      error={operation.error}
      actions={!active ? [{ label: 'Cerrar', onClick: onClose }] : []}
    />
  );
}

function workerOperationTitle(action: AdminSlotAction) {
  const labels: Record<AdminSlotAction, string> = {
    admin_create_slot: 'Comando admin',
    admin_update_slot: 'Comando admin',
    admin_delete_slot: 'Comando admin',
    admin_reset_slot: 'Comando admin',
    admin_select_slot: 'Comando admin',
    admin_start_hosted_workers: 'Arrancando hosted workers',
    admin_stop_hosted_workers: 'Parando hosted workers',
    admin_start_slot_worker: 'Arrancando worker',
    admin_stop_slot_worker: 'Parando worker',
    admin_reconcile_host_capacity: 'Reconciliando capacidad'
  };
  return labels[action] || 'Comando admin';
}

function workerOperationMessage(action: AdminSlotAction, status: WorkerOperationState['status']) {
  if (status === 'preparing') return 'Preparando comando';
  if (status === 'queued') return 'Comando en cola para el bridge';
  if (status === 'delivered') return action === 'admin_reconcile_host_capacity' ? 'Bridge verificando capacidad' : 'Bridge ejecutando procedimiento local';
  if (status === 'completed') {
    if (action === 'admin_reconcile_host_capacity') return 'Capacidad reconciliada';
    return action.includes('stop') ? 'Parada completada' : 'Arranque completado';
  }
  if (status === 'duplicate') return 'Comando duplicado ignorado';
  if (status === 'cancelled') return 'Comando cancelado';
  if (status === 'rejected') return 'Comando rechazado';
  if (status === 'timeout') return 'Sin confirmacion final del bridge';
  return 'El comando fallo';
}

function workerOperationDetail(snapshot: AdminCommandSnapshot) {
  const result = snapshot.result || {};
  const capacity = result.capacity as { status?: string; free_count?: number; assigned_count?: number; shortfall?: number } | undefined;
  const launcher = typeof result.launcher === 'string' ? result.launcher : '';
  const error = snapshot.error || (typeof result.error === 'string' ? result.error : '');
  if (error) return error;
  if (capacity) {
    return `capacidad=${capacityLabel(capacity.status)} libres=${capacity.free_count ?? '-'} asignados=${capacity.assigned_count ?? '-'} deficit=${capacity.shortfall ?? '-'}`;
  }
  if (launcher) return `launcher=${launcher}`;
  if (snapshot.deliveredAt && !snapshot.completedAt) return `delivered_at=${snapshot.deliveredAt}`;
  if (snapshot.completedAt) return `completed_at=${snapshot.completedAt}`;
  return `command_id=${snapshot.commandId}`;
}

async function waitForAdminCommandStatus(
  api: BotApi,
  commandId: string,
  action: AdminSlotAction,
  onUpdate: (snapshot: AdminCommandSnapshot) => void
): Promise<AdminCommandSnapshot> {
  const startedAt = Date.now();
  const timeoutMs = action.includes('_hosted_workers') || action.includes('_slot_worker')
    ? adminWorkerCommandTimeoutMs
    : adminCommandDefaultTimeoutMs;
  let lastSnapshot: AdminCommandSnapshot | null = null;
  while (Date.now() - startedAt < timeoutMs) {
    await delay(1500);
    const snapshot = await api.getAdminCommandStatus?.(commandId);
    if (!snapshot) continue;
    lastSnapshot = snapshot;
    onUpdate(snapshot);
    if (adminCommandTerminalStatuses.has(snapshot.status)) return snapshot;
  }
  const lastStatus = lastSnapshot ? ` Ultimo estado: ${lastSnapshot.status}.` : '';
  throw new Error(`Timeout esperando el resultado del comando ${commandId}.${lastStatus}`);
}

async function delay(ms: number) {
  await new Promise((resolve) => window.setTimeout(resolve, ms));
}

function mergeSlotKeys(...groups: readonly (readonly string[])[]): string[] {
  const merged: string[] = [];
  for (const group of groups) {
    for (const raw of group) {
      const slotKey = String(raw || '').trim();
      if (slotKey && !merged.includes(slotKey)) {
        merged.push(slotKey);
      }
    }
  }
  return merged;
}

interface LocalSlotOption {
  slotKey: string;
  agentId: string;
  profile: string;
  health: string;
  instanceName?: string;
  instanceKey?: string;
  deviceId?: string;
  warnings: string[];
}

function localSlotOptionsFromHosts(hosts: readonly AgentHost[]): LocalSlotOption[] {
  const options: LocalSlotOption[] = [];
  const seen = new Set<string>();
  for (const host of hosts) {
    const profiles = host.inventory?.profiles || [];
    for (const profile of profiles) {
      if (profile.reserved) continue;
      const profileSlotKeys = mergeSlotKeys(
        profile.assigned_slots || [],
        canonicalSlotKeyFromProfile(profile) ? [canonicalSlotKeyFromProfile(profile) as string] : []
      );
      for (const slotKey of profileSlotKeys) {
        const normalizedSlotKey = String(slotKey || '').trim();
        if (isReservedLocalSlotKey(normalizedSlotKey)) continue;
        const key = `${host.agentId}:${normalizedSlotKey}`;
        if (normalizedSlotKey && !seen.has(key)) {
          seen.add(key);
          options.push(toLocalSlotOption(host, profile, normalizedSlotKey));
        }
      }
    }
  }
  return options.sort((left, right) => left.slotKey.localeCompare(right.slotKey, undefined, { numeric: true }));
}

function isReservedLocalSlotKey(slotKey: string): boolean {
  return slotKey === 'slot_1';
}

function canonicalSlotKeyFromProfile(profile: BlueStacksProfileHealth): string | null {
  const candidates = [profile.profile, profile.instance_name].map((value) => String(value || '').trim());
  return candidates.find((value) => /^slot_\d+$/i.test(value)) || null;
}

function defaultSlotLabel(user: UserProfile, slot: LocalSlotOption): string {
  const prefix = user.email.split('@')[0] || 'usuario';
  return `${prefix} ${slot.slotKey}`;
}

function toLocalSlotOption(host: AgentHost, profile: BlueStacksProfileHealth, slotKey: string): LocalSlotOption {
  return {
    slotKey,
    agentId: host.agentId,
    profile: profile.profile,
    health: profile.health || 'unknown',
    instanceName: profile.instance_name,
    instanceKey: profile.instance_key,
    deviceId: profile.device_id,
    warnings: profile.warnings || []
  };
}


function formatDateTimeForLog(value?: string | null) {
  if (!value) return '---- -- -- --:--:--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (input: number) => String(input).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function shortId(value: string) {
  return value.length > 12 ? `${value.slice(0, 8)}...` : value;
}

type ProvisioningStatus = 'ready' | 'warning' | 'blocked';

interface ProvisioningResult {
  status: ProvisioningStatus;
  label: string;
  items: { label: string; ok: boolean; detail?: string }[];
}

function buildProvisioningChecklist({
  slot,
  host,
  commandSlotKeys,
  formSlotKey
}: {
  slot?: BotSlot;
  host?: AgentHost;
  commandSlotKeys: readonly string[];
  formSlotKey: string;
}): ProvisioningResult {
  const inventory = host?.inventory || {};
  const profiles = inventory.profiles || [];
  const selectedProfile = profiles.find((profile) => profile.assigned_slots?.includes(formSlotKey));
  const hostFresh = isFresh(host?.lastSeenAt, 45);
  const inventoryFresh = isFresh(host?.inventoryUpdatedAt, 90);
  const slotAvailable = commandSlotKeys.includes(formSlotKey);
  const slotReady = Boolean(slot?.licenseStatus && ['active', 'trial'].includes(slot.licenseStatus) && slot.linked);
  const profileReady = selectedProfile?.health === 'ready';
  const namingReady = Boolean(
    selectedProfile &&
      selectedProfile.profile === formSlotKey &&
      selectedProfile.instance_name === formSlotKey &&
      selectedProfile.assigned_slots?.includes(formSlotKey)
  );

  const items = [
    {
      label: 'Usuario con slot asignado',
      ok: Boolean(slot?.userEmail),
      detail: slot?.userEmail || 'sin usuario'
    },
    {
      label: 'Licencia y vinculacion activas',
      ok: slotReady,
      detail: slot ? `${slot.licenseStatus} / ${slot.linked ? 'linked' : 'unlinked'}` : 'slot no guardado'
    },
    {
      label: 'Slot disponible para comandos hosted',
      ok: slotAvailable,
      detail: slotAvailable
        ? `${formSlotKey} disponible por inventario local o slot remoto`
        : `slots disponibles: ${commandSlotKeys.join(', ') || 'ninguno'}`
    },
    {
      label: 'Agente local online',
      ok: Boolean(host && host.status === 'online' && hostFresh),
      detail: host ? `${host.status} / ${formatAge(host.lastSeenAt)}` : 'sin host'
    },
    {
      label: 'Inventario BlueStacks reciente',
      ok: Boolean(host && inventoryFresh),
      detail: formatAge(host?.inventoryUpdatedAt)
    },
    {
      label: 'Nombre unificado slot/profile/instancia',
      ok: namingReady,
      detail: selectedProfile
        ? `${formSlotKey} / ${selectedProfile.profile || '-'} / ${selectedProfile.instance_name || '-'}`
        : 'sin perfil local para este slot'
    },
    {
      label: 'Perfil BlueStacks dedicado y sano',
      ok: Boolean(profileReady),
      detail: selectedProfile
        ? `${selectedProfile.profile} / ${selectedProfile.health}${selectedProfile.warnings?.length ? ` / ${selectedProfile.warnings.join(' | ')}` : ''}`
        : 'sin perfil local para este slot'
    }
  ];

  const hardFailures = items.filter((item) => !item.ok && item.label !== 'Perfil BlueStacks dedicado y sano');
  if (hardFailures.length) {
    return { status: 'blocked', label: 'bloqueado', items };
  }
  if (!profileReady) {
    return { status: 'warning', label: 'requiere revision', items };
  }
  return { status: 'ready', label: 'listo', items };
}

function isFresh(value?: string | null, maxAgeSeconds = 60) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() <= maxAgeSeconds * 1000;
}

function capacityLabel(status?: string | null) {
  if (status === 'ready') return 'lista';
  if (status === 'needs_capacity') return 'necesita slots';
  if (status === 'unknown') return 'sin confirmar';
  return status || 'sin datos';
}

function formatAge(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const ageSeconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (ageSeconds < 60) return `${ageSeconds}s`;
  const minutes = Math.floor(ageSeconds / 60);
  const seconds = ageSeconds % 60;
  return `${minutes}m ${seconds}s`;
}
